import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { FiPrWordmark } from '../components/FiPrLogo';
import { User } from 'lucide-react';
import OtpInput from '../components/OtpInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 3-step signup flow:
 *   step 1 = "email"    — enter email + click Send Code
 *   step 2 = "otp"      — enter 6-digit code from email (resend cooldown 60s)
 *   step 3 = "details"  — password + full_name + role + department + create
 *
 * The verify_token minted at step 2 is required by /api/auth/register.
 * Legacy beta-invite flow (?invite=...&email=...) is preserved but SKIPS the
 * OTP steps since the invite itself is proof-of-email.
 *
 * NOTE: legacy invite path currently mints no verify_token — server-side
 * gate rejects. Fix: on invite-valid we call /signup-otp/send + a preset code
 * doesn't exist, so we let user complete OTP as normal but with prefilled
 * email. This is a minor UX regression for beta-invited users, worth the
 * SEC posture.
 */
const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();

  const [step, setStep] = useState('email'); // 'email' | 'otp' | 'details'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [verifyToken, setVerifyToken] = useState('');
  const [details, setDetails] = useState({
    password: '', full_name: '', role: 'PRODUCER', department: 'PRODUCTION',
  });
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Invite flow
  const inviteToken = searchParams.get('invite');
  const inviteEmail = searchParams.get('email');
  const [inviteValid, setInviteValid] = useState(null);
  const [inviteError, setInviteError] = useState('');

  useEffect(() => {
    if (inviteToken && inviteEmail) {
      const decoded = decodeURIComponent(inviteEmail);
      setEmail(decoded);
      axios.get(`${API}/beta/validate-invite?token=${inviteToken}&email=${encodeURIComponent(decoded)}`)
        .then(({ data }) => {
          setInviteValid(true);
          if (data.name) setDetails(d => ({ ...d, full_name: data.name }));
        })
        .catch(err => {
          setInviteValid(false);
          setInviteError(err.response?.data?.detail || 'Invalid or expired invite link');
        });
    }
  }, [inviteToken, inviteEmail]);

  // Resend cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleSendCode = async (e) => {
    if (e) e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/auth/signup-otp/send`, { email: email.trim().toLowerCase() });
      toast.success('Verification code sent — check your inbox.');
      setStep('otp');
      setCooldown(60);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send code');
    } finally { setBusy(false); }
  };

  const handleVerifyCode = async (e) => {
    if (e) e.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/signup-otp/verify`, {
        email: email.trim().toLowerCase(),
        code,
      });
      setVerifyToken(data.verify_token);
      toast.success('Email verified.');
      setStep('details');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally { setBusy(false); }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!verifyToken) {
      toast.error('Email not verified. Restart signup.');
      setStep('email');
      return;
    }
    setBusy(true);
    const payload = {
      email: email.trim().toLowerCase(),
      password: details.password,
      full_name: details.full_name,
      role: details.role,
      department: details.department,
      verify_token: verifyToken,
    };
    const result = await register(payload);
    if (result.success) {
      if (inviteToken && inviteEmail) {
        try {
          await axios.post(`${API}/beta/mark-invite-used?token=${inviteToken}&email=${encodeURIComponent(decodeURIComponent(inviteEmail))}`);
        } catch (err) { console.error('register.mark-invite-used:', err); }
      }
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
    setBusy(false);
  };

  // ── UI helpers ───────────────────────────────────────────────
  const InviteBanner = () => (
    <>
      {inviteToken && inviteValid === null && (
        <div className="mb-4 p-3 rounded-lg bg-[#F5A623]/10 border border-[#F5A623]/30 text-center">
          <p className="text-sm text-[#F5A623]">Validating your invite link…</p>
        </div>
      )}
      {inviteToken && inviteValid === true && (
        <div className="mb-4 p-3 rounded-lg bg-[#34d399]/10 border border-[#34d399]/30 flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-[#34d399] flex-shrink-0" />
          <p className="text-sm text-[#34d399]">Valid beta invite — welcome to FiPr.ai!</p>
        </div>
      )}
      {inviteValid === false && (
        <div className="mb-4 p-3 rounded-lg bg-[#f87171]/10 border border-[#f87171]/30 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-[#f87171] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-[#f87171]">{inviteError}</p>
            <a href="/" className="text-xs text-[#F5A623] underline mt-1 inline-block">Apply for beta access →</a>
          </div>
        </div>
      )}
    </>
  );

  const disabled = inviteValid === false;

  return (
    <div className="min-h-screen bg-[#0D0F14] flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md bg-[#13151C] border-[#1E2028] p-8 rounded-lg" data-testid="register-form">
        <div className="flex justify-center mb-6">
          <FiPrWordmark iconSize={44} textSize="3xl" />
        </div>
        <h1 className="font-semibold text-3xl text-[#FFFFFF] text-center mb-2">Create Account</h1>

        <InviteBanner />

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-6" data-testid="step-indicator">
          {['email', 'otp', 'details'].map((s, i) => (
            <React.Fragment key={s}>
              <div
                className="text-[10px] font-semibold px-2 py-1 rounded-full uppercase tracking-wider"
                style={{
                  background: step === s ? '#F5A623' : (['email', 'otp', 'details'].indexOf(step) > i ? 'rgba(52,211,153,.15)' : '#1E2028'),
                  color: step === s ? '#000' : (['email', 'otp', 'details'].indexOf(step) > i ? '#34d399' : '#6C7086'),
                }}
              >{i + 1}. {s}</div>
              {i < 2 && <div className="w-4 h-px bg-[#1E2028]" />}
            </React.Fragment>
          ))}
        </div>

        {/* ── Step 1 : Email ─────────────────────────────────────── */}
        {step === 'email' && (
          <form onSubmit={handleSendCode} className={`space-y-5 ${disabled ? 'opacity-40 pointer-events-none' : ''}`} data-testid="step-email">
            <p className="text-[#A0A4B8] text-center text-sm mb-2">
              Enter your email to receive a 6-digit verification code.
            </p>
            <div>
              <label className="text-[#FFFFFF] text-sm mb-2 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#A0A4B8]" />
                <Input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="pl-10 bg-[#0D0F14] border-[#1E2028] text-[#FFFFFF] focus:border-[#F5A623] rounded-lg"
                  placeholder="your@email.com"
                  required
                  autoFocus
                  data-testid="email-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={busy || !email}
              className="w-full bg-[#F5A623] hover:bg-[#E0961B] text-black font-medium py-6 rounded-lg"
              data-testid="send-otp-btn"
            >{busy ? 'Sending…' : 'Send verification code'}</Button>
          </form>
        )}

        {/* ── Step 2 : OTP ───────────────────────────────────────── */}
        {step === 'otp' && (
          <form onSubmit={handleVerifyCode} className="space-y-5" data-testid="step-otp">
            <p className="text-[#A0A4B8] text-center text-sm">
              We sent a 6-digit code to <span className="text-[#F5A623] font-semibold">{email}</span>.
            </p>
            <OtpInput value={code} onChange={setCode} disabled={busy} />
            <Button
              type="submit"
              disabled={busy || code.length !== 6}
              className="w-full bg-[#F5A623] hover:bg-[#E0961B] text-black font-medium py-6 rounded-lg"
              data-testid="verify-otp-btn"
            >{busy ? 'Verifying…' : 'Verify code'}</Button>
            <div className="flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={() => { setStep('email'); setCode(''); }}
                className="text-[#A0A4B8] hover:text-[#FFFFFF] flex items-center gap-1"
                data-testid="otp-back-btn"
              ><ArrowLeft className="h-3 w-3" /> Change email</button>
              <button
                type="button"
                disabled={cooldown > 0 || busy}
                onClick={handleSendCode}
                className="text-[#F5A623] hover:text-[#E0961B] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="otp-resend-btn"
              >{cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}</button>
            </div>
          </form>
        )}

        {/* ── Step 3 : Details ───────────────────────────────────── */}
        {step === 'details' && (
          <form onSubmit={handleCreateAccount} className="space-y-5" data-testid="step-details">
            <p className="text-[#A0A4B8] text-center text-sm mb-2 flex items-center gap-1.5 justify-center">
              <CheckCircle className="h-4 w-4 text-[#34d399]" />
              <span><span className="text-[#F5A623]">{email}</span> verified — finish your account.</span>
            </p>
            <div>
              <label className="text-[#FFFFFF] text-sm mb-2 block">Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#A0A4B8]" />
                <Input
                  type="text"
                  value={details.full_name}
                  onChange={e => setDetails({ ...details, full_name: e.target.value })}
                  className="pl-10 bg-[#0D0F14] border-[#1E2028] text-[#FFFFFF] focus:border-[#F5A623] rounded-lg"
                  placeholder="John Doe"
                  required
                  data-testid="fullname-input"
                />
              </div>
            </div>
            <div>
              <label className="text-[#FFFFFF] text-sm mb-2 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#A0A4B8]" />
                <Input
                  type="password"
                  value={details.password}
                  onChange={e => setDetails({ ...details, password: e.target.value })}
                  className="pl-10 bg-[#0D0F14] border-[#1E2028] text-[#FFFFFF] focus:border-[#F5A623] rounded-lg"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>
            </div>
            <div>
              <label className="text-[#FFFFFF] text-sm mb-2 block">Role</label>
              <Select value={details.role} onValueChange={v => setDetails({ ...details, role: v })}>
                <SelectTrigger className="bg-[#0D0F14] border-[#1E2028] text-[#FFFFFF] focus:border-[#F5A623] rounded-lg" data-testid="role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#13151C] border-[#1E2028] text-[#FFFFFF]">
                  <SelectItem value="PRODUCER">Producer</SelectItem>
                  <SelectItem value="PRODUCTION_MANAGER">Production Manager</SelectItem>
                  <SelectItem value="DEPARTMENT_HEAD">Department Head</SelectItem>
                  <SelectItem value="DEPARTMENT_ASSISTANT">Department Assistant</SelectItem>
                  <SelectItem value="DIRECTOR">Director</SelectItem>
                  <SelectItem value="ASSISTANT_DIRECTOR">Assistant Director (AD)</SelectItem>
                  <SelectItem value="ASSOCIATE_DIRECTOR">Associate Director</SelectItem>
                  <SelectItem value="CINEMATOGRAPHER">Cinematographer (DP)</SelectItem>
                  <SelectItem value="EDITOR">Editor</SelectItem>
                  <SelectItem value="VIEWER">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[#FFFFFF] text-sm mb-2 block">Department</label>
              <Select value={details.department} onValueChange={v => setDetails({ ...details, department: v })}>
                <SelectTrigger className="bg-[#0D0F14] border-[#1E2028] text-[#FFFFFF] focus:border-[#F5A623] rounded-lg" data-testid="department-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#13151C] border-[#1E2028] text-[#FFFFFF]">
                  <SelectItem value="PRODUCTION">Production</SelectItem>
                  <SelectItem value="DIRECTION">Direction</SelectItem>
                  <SelectItem value="ART">Art Department</SelectItem>
                  <SelectItem value="COSTUME_WARDROBE">Costume/Wardrobe</SelectItem>
                  <SelectItem value="CASTING">Casting</SelectItem>
                  <SelectItem value="MAKEUP_HAIR">Makeup/Hair</SelectItem>
                  <SelectItem value="VFX">VFX</SelectItem>
                  <SelectItem value="LOCATIONS">Locations</SelectItem>
                  <SelectItem value="SFX_STUNTS">SFX/Stunts</SelectItem>
                  <SelectItem value="SOUND">Sound</SelectItem>
                  <SelectItem value="CAMERA">Camera</SelectItem>
                  <SelectItem value="EDITING">Editing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-[#F5A623] hover:bg-[#E0961B] text-black font-medium py-6 rounded-lg"
              data-testid="register-submit-btn"
            >{busy ? 'Creating Account...' : 'Create Account'}</Button>
          </form>
        )}

        {/* ── or-divider + Google OAuth (always visible) ─────────── */}
        <div className="flex items-center gap-3 my-5" aria-hidden="true">
          <div className="flex-1 h-px bg-[#1E2028]" />
          <span className="text-xs text-[#6C7086] uppercase tracking-wider">or</span>
          <div className="flex-1 h-px bg-[#1E2028]" />
        </div>
        <Button
          type="button"
          onClick={() => {
            const cb = encodeURIComponent(`${window.location.origin}/`);
            window.location.href = `https://auth.emergentagent.com/?redirect=${cb}`;
          }}
          variant="outline"
          className="w-full bg-white hover:bg-gray-100 text-gray-800 border border-gray-300 font-medium py-6 rounded-lg flex items-center justify-center gap-3"
          data-testid="continue-with-google-btn"
        >
          <svg className="h-5 w-5" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
            <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
            <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
          </svg>
          Continue with Google
        </Button>

        <div className="mt-6 text-center">
          <p className="text-[#A0A4B8] text-sm">
            Already have an account?{' '}
            <Link to="/auth" className="text-[#F5A623] hover:text-[#E0961B]" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>

        <div className="mt-4 text-center">
          <Link to="/" className="text-[#A0A4B8] hover:text-[#FFFFFF] text-sm" data-testid="back-home-link">
            ← Back to Home
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default RegisterPage;
